import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarIcon from "@mui/icons-material/Star";
import ChatIcon from "@mui/icons-material/Chat";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import { getSharedNoteById } from "../../firebase/notesService";
import { useSocial } from "../../context/SocialContext";
import { chapterPathWithVerse, cleanVerseQuoteText, formatBookName } from "../../bible/verseQuote";

const SharedNoteDetail = () => {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const { openUserCard, openChatWithUser } = useSocial();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState("");

  useEffect(() => {
    const fetchNote = async () => {
      setLoading(true);
      const data = await getSharedNoteById(noteId);
      setNote(data);
      setLoading(false);
    };
    fetchNote();
  }, [noteId]);

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setToastMsg("分享链接已复制！");
    }
  };

  const handleCopyContent = () => {
    if (!note) return;
    const text = `【${note.title}】\n经文：${formatBookName(note.bookName)} 第${note.chapterNumber}章${
      note.verseRange ? `:${note.verseRange}` : ""
    }\n摘录：${cleanVerseQuoteText(note.verseText)}\n心得：${note.content}\n—— 读经读者：${
      note.userDisplayName || "主内读者"
    }`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setToastMsg("经文与笔记内容已复制！");
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          正在加载分享笔记...
        </Typography>
      </Container>
    );
  }

  if (!note) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          未找到该分享笔记
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          该笔记可能已被作者设为私密或已删除。
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/community")}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          返回社区灵修墙
        </Button>
      </Container>
    );
  }

  const author = note.userDisplayName || "主内读者";
  const initial = author[0] || "主";
  const chapterHref = chapterPathWithVerse(note.chapterId, note.verseRange);
  const ref = `${formatBookName(note.bookName) || ""} 第${note.chapterNumber || 1}章${
    note.verseRange ? `:${note.verseRange}` : ""
  }`;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/community")}
        size="small"
        sx={{ mb: 2.5, textTransform: "none", fontWeight: 700 }}
      >
        返回社区灵修分享
      </Button>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 4 },
          borderRadius: 3.5,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
        }}
      >
        {/* Author info */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            cursor: (note.authorId || note.userId) ? "pointer" : "default",
            width: "fit-content",
            transition: "opacity 0.15s ease",
            "&:hover": (note.authorId || note.userId) ? { opacity: 0.82 } : {},
          }}
          onClick={() => {
            const authorUid = note.authorId || note.userId;
            if (authorUid) {
              openUserCard(authorUid, {
                displayName: author,
                photoURL: note.authorPhotoURL,
              });
            }
          }}
        >
          <Avatar
            src={note.authorPhotoURL}
            sx={{
              width: 44,
              height: 44,
              bgcolor: "primary.main",
              fontWeight: 800,
              transition: "transform 0.15s ease",
              "&:hover": { transform: "scale(1.08)" },
            }}
          >
            {initial}
          </Avatar>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  "&:hover": { textDecoration: (note.authorId || note.userId) ? "underline" : "none" },
                }}
              >
                {author}
              </Typography>
              {note.featured && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: "14px !important" }} />}
                  label="精选分享"
                  size="small"
                  color="primary"
                  sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              分享于：
              {note.updatedAtIso || note.createdAtIso
                ? new Date(
                    note.updatedAtIso || note.createdAtIso
                  ).toLocaleDateString()
                : "近期"}
            </Typography>
          </Box>
        </Box>

        {/* Bible Reference Pill */}
        <Box sx={{ mb: 2.5 }}>
          <Chip
            label={ref}
            color="primary"
            size="medium"
            onClick={() => navigate(chapterHref)}
            sx={{
              fontWeight: 800,
              fontSize: "0.9rem",
              cursor: "pointer",
            }}
          />
        </Box>

        {/* Note Title */}
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
          {note.title}
        </Typography>

        {/* Scripture Quote */}
        {note.verseText && (
          <Box
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 2.5,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.02)",
              borderLeft: "4px solid",
              borderLeftColor: "primary.main",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontStyle: "italic",
                color: "text.primary",
                lineHeight: 1.7,
              }}
            >
              {cleanVerseQuoteText(note.verseText)}
            </Typography>
          </Box>
        )}

        {/* Content */}
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.8,
            whiteSpace: "pre-line",
            color: "text.primary",
            mb: 3,
            fontSize: "1.05rem",
          }}
        >
          {note.content}
        </Typography>

        {/* Tags */}
        {note.tags && note.tags.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 3 }}>
            {note.tags.map((t) => (
              <Chip
                key={t}
                label={`#${t}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AutoStoriesIcon />}
            onClick={() => navigate(chapterHref)}
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
          >
            在圣经中阅读此章节
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            {(note.userId || note.authorId) && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<ContactPageIcon />}
                  onClick={() =>
                    openUserCard(note.authorId || note.userId, {
                      displayName: note.userDisplayName,
                      photoURL: note.authorPhotoURL,
                    })
                  }
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  主内名片
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<ChatIcon />}
                  onClick={() =>
                    openChatWithUser(
                      {
                        uid: note.authorId || note.userId,
                        displayName: note.userDisplayName,
                        photoURL: note.authorPhotoURL,
                      },
                      {
                        verseQuote: `${note.bookName || ""} 第${note.chapterNumber || 1}章${
                          note.verseRange ? `:${note.verseRange}` : ""
                        }`,
                      }
                    )
                  }
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  私信交流
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyContent}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              复制内容
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ShareIcon />}
              onClick={handleCopyLink}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              分享链接
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={Boolean(toastMsg)}
        autoHideDuration={3000}
        onClose={() => setToastMsg("")}
        message={toastMsg}
      />
    </Container>
  );
};

export default SharedNoteDetail;
